/**
 * Money handling.
 *
 * Rule for the whole codebase: money is NEVER a JavaScript `number`.
 *
 * - In Postgres it is `numeric(18,2)`.
 * - Over the wire it is a decimal *string* ("1234.56").
 * - In arithmetic it is a `bigint` of minor units (kobo, cents, …).
 *
 * `0.1 + 0.2 !== 0.3` is an accounting bug waiting to happen, and a financial
 * record system is exactly the wrong place to discover it.
 */

/** All supported currencies are 2-decimal. Revisit if JPY/KWD are ever added. */
export const MONEY_SCALE = 2;
const MINOR_FACTOR = 100n;

/** Largest value representable in numeric(18,2): 9,999,999,999,999,999.99 */
export const MAX_MINOR_UNITS = 10n ** 18n - 1n;

export class MoneyError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'MoneyError';
  }
}

const DECIMAL_RE = /^-?\d{1,16}(\.\d{1,2})?$/;

/**
 * Parse a decimal string (or safe integer) into minor units.
 * Rejects anything that is not an exact 2-decimal value — we never silently
 * round a user's amount.
 */
export function toMinor(value: string | number | bigint): bigint {
  if (typeof value === 'bigint') return value;

  const raw = typeof value === 'number' ? numberToDecimalString(value) : value.trim();

  if (!DECIMAL_RE.test(raw)) {
    throw new MoneyError(
      `Invalid money value "${raw}": expected a decimal with at most ${MONEY_SCALE} places`,
    );
  }

  const negative = raw.startsWith('-');
  const unsigned = negative ? raw.slice(1) : raw;
  const [whole = '0', frac = ''] = unsigned.split('.');
  const padded = frac.padEnd(MONEY_SCALE, '0');

  const minor = BigInt(whole) * MINOR_FACTOR + BigInt(padded || '0');
  if (minor > MAX_MINOR_UNITS) {
    throw new MoneyError(`Money value "${raw}" exceeds the maximum supported amount`);
  }
  return negative ? -minor : minor;
}

function numberToDecimalString(value: number): string {
  if (!Number.isFinite(value)) {
    throw new MoneyError(`Invalid money value "${value}": not a finite number`);
  }
  return value.toFixed(MONEY_SCALE);
}

/** Render minor units back to the canonical decimal string used on the wire. */
export function toDecimalString(minor: bigint): string {
  const negative = minor < 0n;
  const abs = negative ? -minor : minor;
  const whole = abs / MINOR_FACTOR;
  const frac = abs % MINOR_FACTOR;
  return `${negative ? '-' : ''}${whole}.${frac.toString().padStart(MONEY_SCALE, '0')}`;
}

/**
 * Normalise any incoming money representation to the canonical wire string.
 * Postgres `numeric` arrives from `pg` as a string, so this is also what
 * repositories run row values through.
 */
export function normalizeAmount(value: string | number | bigint | null | undefined): string {
  if (value === null || value === undefined) return '0.00';
  return toDecimalString(toMinor(value));
}

export function addAmounts(...values: Array<string | number | bigint>): string {
  return toDecimalString(values.reduce<bigint>((sum, v) => sum + toMinor(v), 0n));
}

export function subtractAmounts(a: string | number | bigint, b: string | number | bigint): string {
  return toDecimalString(toMinor(a) - toMinor(b));
}

export function compareAmounts(a: string | number | bigint, b: string | number | bigint): -1 | 0 | 1 {
  const left = toMinor(a);
  const right = toMinor(b);
  return left < right ? -1 : left > right ? 1 : 0;
}

export function isZero(value: string | number | bigint): boolean {
  return toMinor(value) === 0n;
}

export function isPositive(value: string | number | bigint): boolean {
  return toMinor(value) > 0n;
}

/**
 * Percentage of `part` relative to `whole`, as a number rounded to one decimal.
 * Returns null when `whole` is zero — "0% of nothing" is meaningless and the
 * UI should render a dash instead.
 */
export function percentOf(
  part: string | number | bigint,
  whole: string | number | bigint,
): number | null {
  const w = toMinor(whole);
  if (w === 0n) return null;
  const p = toMinor(part);
  return Math.round((Number(p) / Number(w)) * 1000) / 10;
}

/** Human-facing formatting. Falls back to a plain code prefix for exotic currencies. */
export function formatMoney(
  value: string | number | bigint,
  currencyCode: string,
  locale = 'en-US',
): string {
  const decimal = toDecimalString(toMinor(value));
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: MONEY_SCALE,
      maximumFractionDigits: MONEY_SCALE,
    }).format(Number(decimal));
  } catch {
    return `${currencyCode} ${decimal}`;
  }
}
